import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/AdminSidebar';
import BilingualInput from '../../components/admin/BilingualInput';
import BilingualTextArea from '../../components/admin/BilingualTextArea';
import {
    AlertCircle,
    Briefcase,
    Building2,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    DollarSign,
    FileText,
    HardHat,
    Home,
    Image as ImageIcon,
    Info,
    Layout,
    Loader2,
    LogOut,
    Mail,
    MapPin,
    Mountain,
    PanelBottom,
    Plane,
    RefreshCw,
    Save,
    Target,
    Truck,
    Type,
    Users,
    Zap
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const AdminCareers: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'text' | 'media'>('text');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState<string | null>(null);
    const [content, setContent] = useState<any>({ Careers: {} });
    const [expandedSections, setExpandedSections] = useState<string[]>(['Recruitment Portal', 'Recruitment Alerts']);
    const [pendingImages, setPendingImages] = useState<{ [key: string]: { file: File, preview: string } }>({});
    const [message, setMessage] = useState<{ text: string, type: 'success' | 'error' | '' }>({ text: '', type: '' });
    const username = localStorage.getItem('adminUser') || 'Admin';

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (!token) {
            navigate('/admin/login');
        } else {
            fetchContent();
        }
    }, [navigate]);

    const fetchContent = async (background = false) => {
        if (!background) setLoading(true);
        try {
            const response = await fetch('/api/homepage/content');
            const data = await response.json();
            if (!data.Careers) data.Careers = {};
            setContent(data);
        } catch (err) {
            console.error('Failed to fetch content');
        } finally {
            if (!background) setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
    };

    const updateField = (key: string, value: any) => {
        setContent((prev: any) => ({
            ...prev,
            Careers: {
                ...prev.Careers,
                [key]: value
            }
        }));
    };

    const updateFieldAr = (key: string, value: any) => {
        setContent((prev: any) => ({
            ...prev,
            Careers: {
                ...prev.Careers,
                [`${key}_ar`]: value
            }
        }));
    };

    const saveSectionText = async (sectionId: string) => {
        setSaving(sectionId);
        try {
            const careersData = content.Careers || {};
            const fieldsToSave: { [key: string]: string } = {};

            if (sectionId === 'hero') {
                fieldsToSave['hero_top_line'] = careersData.hero_top_line ?? '';
                fieldsToSave['hero_title'] = careersData.hero_title ?? '';
                fieldsToSave['hero_desc'] = careersData.hero_desc ?? '';
                fieldsToSave['hero_btn_text'] = careersData.hero_btn_text ?? '';
            } else if (sectionId === 'contact') {
                fieldsToSave['sidebar_title'] = careersData.sidebar_title ?? '';
                fieldsToSave['sidebar_email'] = careersData.sidebar_email ?? '';
                fieldsToSave['cv_text'] = careersData.cv_text ?? '';
                fieldsToSave['sidebar_desc'] = careersData.sidebar_desc ?? '';
            } else if (sectionId === 'notice') {
                fieldsToSave['notice_title'] = careersData.notice_title ?? '';
                fieldsToSave['notice_btn_text'] = careersData.notice_btn_text ?? '';
                fieldsToSave['notice_desc'] = careersData.notice_desc ?? '';
                fieldsToSave['reminder_title'] = careersData.reminder_title ?? '';
                fieldsToSave['reminder_btn_text'] = careersData.reminder_btn_text ?? '';
                fieldsToSave['reminder_desc'] = careersData.reminder_desc ?? '';
            } else if (sectionId === 'paragraphs') {
                [1, 2, 3, 4].forEach(i => {
                    fieldsToSave[`content_p${i}`] = careersData[`content_p${i}`] ?? '';
                });
            } else if (sectionId === 'opp') {
                fieldsToSave['opp_title'] = careersData.opp_title ?? '';
                fieldsToSave['opp_desc'] = careersData.opp_desc ?? '';
                fieldsToSave['opp_btn_text'] = careersData.opp_btn_text ?? '';
            }

            const promises = Object.entries(fieldsToSave).map(async ([key, value]) => {
                return fetch('/api/homepage/update-text', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                    },
                    body: JSON.stringify({ 
                        section: 'Careers', 
                        content_key: key, 
                        content_value: value,
                        content_value_ar: careersData[`${key}_ar`] || null 
                    })
                });
            });

            await Promise.all(promises);
            setMessage({ text: 'Recruitment data synchronized!', type: 'success' });
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);
        } catch (err) {
            setMessage({ text: 'Save operation failed.', type: 'error' });
        } finally {
            setSaving(null);
        }
    };

    const handleImageSelect = (key: string, file: File) => {
        const preview = URL.createObjectURL(file);
        setPendingImages(prev => ({ ...prev, [key]: { file, preview } }));
    };

    const saveAllPendingImages = async () => {
        if (Object.keys(pendingImages).length === 0) return;

        setSaving('all-images');
        try {
            const promises = Object.entries(pendingImages).map(async ([key, pending]) => {
                const formData = new FormData();
                formData.append('section', 'Careers');
                formData.append('content_key', key);
                formData.append('image', pending.file);
                return fetch('/api/homepage/update-image', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` },
                    body: formData
                });
            });

            await Promise.all(promises);
            await fetchContent(true);
            setPendingImages({});
            setMessage({ text: 'Media repository updated!', type: 'success' });
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);
        } catch (err) {
            setMessage({ text: 'Failed to push media assets.', type: 'error' });
        } finally {
            setSaving(null);
        }
    };

    const toggleSection = (name: string) => {
        setExpandedSections(prev =>
            prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name]
        );
    };

    const SectionHeader = ({ title, icon: Icon, sectionId }: any) => (
        <div className="flex items-center justify-between p-6 bg-white rounded-3xl border border-slate-100 shadow-sm mb-4">
            <div className="flex items-center space-x-4 cursor-pointer" onClick={() => toggleSection(title)}>
                <div className={`p-3 rounded-2xl transition-colors ${expandedSections.includes(title) ? 'bg-blue-600 text-white shadow-lg shadow-blue-100' : 'bg-blue-50 text-blue-600'}`}>
                    <Icon className="w-6 h-6" />
                </div>
                <div>
                    <h2 className="text-xl font-black text-slate-800 tracking-tight">{title}</h2>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">Section Configuration</p>
                </div>
                {expandedSections.includes(title) ? <ChevronUp className="w-5 h-5 text-slate-300" /> : <ChevronDown className="w-5 h-5 text-slate-300" />}
            </div>
            {sectionId && (
                <button
                    onClick={() => saveSectionText(sectionId)}
                    disabled={!!saving}
                    className="flex items-center space-x-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-200 text-white rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-xl shadow-emerald-100 active:scale-95"
                >
                    {saving === sectionId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    <span>Sync Section</span>
                </button>
            )}
        </div>
    );

    const Label = ({ children }: any) => (
        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 block">{children}</label>
    );

    const UploadBox = ({ keyName, label, currentImage }: any) => {
        const pending = pendingImages[keyName];
        const displayImage = pending ? pending.preview : (currentImage ? (currentImage.startsWith('http') ? currentImage : `https://hamdanoman.com${currentImage}`) : null);
        return (
            <div className="group bg-white p-6 rounded-3xl border border-slate-100 shadow-sm hover:border-blue-200 transition-all">
                <div className="flex justify-between items-center mb-6">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</span>
                    {currentImage && !pending && (
                        <div className="flex items-center gap-1.5 text-emerald-500 bg-emerald-50 px-2 py-1 rounded-lg">
                            <CheckCircle2 className="w-3 h-3" />
                            <span className="text-[8px] font-black uppercase">Live</span>
                        </div>
                    )}
                </div>
                <div className="relative aspect-video bg-slate-50 rounded-2xl overflow-hidden mb-5 border border-slate-100 flex items-center justify-center ring-offset-4 ring-blue-500/0 group-hover:ring-4 transition-all">
                    {displayImage ? (
                        <img src={displayImage} className="w-full h-full object-cover transition-transform group-hover:scale-105" alt="Preview" />
                    ) : (
                        <div className="flex flex-col items-center gap-2">
                            <ImageIcon className="w-8 h-8 text-slate-200" />
                            <span className="text-[10px] text-slate-300 font-bold uppercase">No Image</span>
                        </div>
                    )}
                    <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[1px]">
                        <label className="cursor-pointer bg-white text-blue-600 p-4 rounded-2xl font-bold font-sans shadow-2xl flex items-center space-x-2 transition-transform hover:scale-105">
                            <RefreshCw className="w-4 h-4" />
                            <input type="file" className="hidden" accept="image/*" onChange={e => e.target.files?.[0] && handleImageSelect(keyName, e.target.files[0])} />
                        </label>
                    </div>
                </div>
                {pending && <p className="text-amber-500 text-[8px] font-black uppercase text-center animate-pulse">Changes Detected</p>}
            </div>
        );
    };

    const navItems = [
        { id: 'dashboard', label: 'Homepage Editor', icon: Home, path: '/admin/dashboard' },
        { id: 'header', label: 'Header Editor', icon: Type, path: '/admin/header' },
        { id: 'footer', label: 'Footer Editor', icon: PanelBottom, path: '/admin/footer' },
        { id: 'contact', label: 'Contact Editor', icon: Mail, path: '/admin/contact' },
        { id: 'about', label: 'About Group Editor', icon: Info, path: '/admin/about' },
        { id: 'careers', label: 'Careers Editor', icon: Briefcase, path: '/admin/careers' },
        { id: 'mining', label: 'Mining Editor', icon: Mountain, path: '/admin/mining' },
        { id: 'transport', label: 'Transport Editor', icon: Truck, path: '/admin/transport' },
        { id: 'construction', label: 'Construction Editor', icon: HardHat, path: '/admin/construction' },
        { id: 'exchange', label: 'Exchange Editor', icon: DollarSign, path: '/admin/exchange' },
        { id: 'gabbro', label: 'Gabbro Editor', icon: Zap, path: '/admin/gabbro' },
        { id: 'plaza', label: 'Plaza Hotel Editor', icon: Building2, path: '/admin/plaza' },
        { id: 'realestate', label: 'Real Estate Editor', icon: MapPin, path: '/admin/realestate' },
        { id: 'tyres', label: 'Tyres Editor', icon: Target, path: '/admin/tyres' },
        { id: 'travel', label: 'Travel Editor', icon: Plane, path: '/admin/travel' },
    ];

    return (
        <div className="flex h-screen bg-slate-50 font-sans overflow-hidden">
            <AdminSidebar />

            {/* Standard Main Area */}
            <div className="flex-1 flex flex-col h-full bg-slate-50/50 relative">
                <header className="h-20 bg-white border-b border-slate-200 flex items-center justify-between px-10 shadow-sm shrink-0 z-30">
                    <div className="flex items-center space-x-6">
                        <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2">
                            <Briefcase className="w-6 h-6 text-blue-600" /> Careers Editor
                        </h2>

                        <div className="flex bg-slate-100 p-1.5 rounded-2xl">
                            <button
                                onClick={() => setActiveTab('text')}
                                className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'text' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <Type className="w-4 h-4" />
                                <span>Content Layout</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('media')}
                                className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'media' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <ImageIcon className="w-4 h-4" />
                                <span>Media Assets</span>
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center space-x-6">
                        {saving && (
                            <div className="flex items-center space-x-2 text-blue-600 font-bold text-xs uppercase tracking-tighter">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                <span className="animate-pulse">Syncing...</span>
                            </div>
                        )}
                        <div className="hidden sm:block text-right">
                            <div className="text-sm font-bold text-slate-900">{username}</div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Recruitment Admin</div>
                        </div>
                        <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center font-bold text-white border border-slate-700 shadow-inner">
                            {username[0]}
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto p-10 custom-scrollbar scroll-smooth">
                    <div className="max-w-5xl mx-auto">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-40">
                                <div className="w-16 h-16 border-4 border-blue-50 border-t-blue-600 rounded-full animate-spin mb-6" />
                                <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Loading Dataset...</p>
                            </div>
                        ) : (
                            <AnimatePresence mode="wait">
                                {activeTab === 'text' ? (
                                    <motion.div key="text" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6 pb-20">
                                        
                                        <SectionHeader title="Recruitment Portal" icon={Layout} sectionId="hero" />
                                        <AnimatePresence>
                                            {expandedSections.includes('Recruitment Portal') && (
                                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                                    <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6 ml-4 mb-8">
                                                        <div className="space-y-6">
                                                            <BilingualInput 
                                                                label="Hero Accent / Top Line"
                                                                englishValue={content.Careers?.hero_top_line}
                                                                arabicValue={content.Careers?.hero_top_line_ar}
                                                                onChangeEnglish={val => updateField('hero_top_line', val)}
                                                                onChangeArabic={val => updateFieldAr('hero_top_line', val)}
                                                            />
                                                            <BilingualInput 
                                                                label="Hero Action Title"
                                                                englishValue={content.Careers?.hero_title}
                                                                arabicValue={content.Careers?.hero_title_ar}
                                                                onChangeEnglish={val => updateField('hero_title', val)}
                                                                onChangeArabic={val => updateFieldAr('hero_title', val)}
                                                            />
                                                            <BilingualTextArea 
                                                                label="Hero Mission / Description"
                                                                englishValue={content.Careers?.hero_desc}
                                                                arabicValue={content.Careers?.hero_desc_ar}
                                                                onChangeEnglish={val => updateField('hero_desc', val)}
                                                                onChangeArabic={val => updateFieldAr('hero_desc', val)}
                                                                rows={3}
                                                            />
                                                            <BilingualInput 
                                                                label="Portal Button Label"
                                                                englishValue={content.Careers?.hero_btn_text}
                                                                arabicValue={content.Careers?.hero_btn_text_ar}
                                                                onChangeEnglish={val => updateField('hero_btn_text', val)}
                                                                onChangeArabic={val => updateFieldAr('hero_btn_text', val)}
                                                            />
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>

                                        <SectionHeader title="Recruitment Alerts" icon={AlertCircle} sectionId="notice" />
                                        <AnimatePresence>
                                            {expandedSections.includes('Recruitment Alerts') && (
                                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                                    <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-8 ml-4 mb-8">
                                                        <div className="space-y-6">
                                                            <BilingualInput 
                                                                label="Alert Headline"
                                                                englishValue={content.Careers?.notice_title}
                                                                arabicValue={content.Careers?.notice_title_ar}
                                                                onChangeEnglish={val => updateField('notice_title', val)}
                                                                onChangeArabic={val => updateFieldAr('notice_title', val)}
                                                            />
                                                            <BilingualInput 
                                                                label="Alert Action Label"
                                                                englishValue={content.Careers?.notice_btn_text}
                                                                arabicValue={content.Careers?.notice_btn_text_ar}
                                                                onChangeEnglish={val => updateField('notice_btn_text', val)}
                                                                onChangeArabic={val => updateFieldAr('notice_btn_text', val)}
                                                            />
                                                            <BilingualTextArea 
                                                                label="Alert Content / Description"
                                                                englishValue={content.Careers?.notice_desc}
                                                                arabicValue={content.Careers?.notice_desc_ar}
                                                                onChangeEnglish={val => updateField('notice_desc', val)}
                                                                onChangeArabic={val => updateFieldAr('notice_desc', val)}
                                                                rows={2}
                                                            />
                                                        </div>

                                                        <div className="pt-8 border-t border-slate-100 space-y-6">
                                                            <BilingualInput 
                                                                label="Reminder Box Headline"
                                                                englishValue={content.Careers?.reminder_title}
                                                                arabicValue={content.Careers?.reminder_title_ar}
                                                                onChangeEnglish={val => updateField('reminder_title', val)}
                                                                onChangeArabic={val => updateFieldAr('reminder_title', val)}
                                                            />
                                                            <BilingualInput 
                                                                label="Reminder Image Trigger Label"
                                                                englishValue={content.Careers?.reminder_btn_text}
                                                                arabicValue={content.Careers?.reminder_btn_text_ar}
                                                                onChangeEnglish={val => updateField('reminder_btn_text', val)}
                                                                onChangeArabic={val => updateFieldAr('reminder_btn_text', val)}
                                                            />
                                                            <BilingualTextArea 
                                                                label="Reminder Summary"
                                                                englishValue={content.Careers?.reminder_desc}
                                                                arabicValue={content.Careers?.reminder_desc_ar}
                                                                onChangeEnglish={val => updateField('reminder_desc', val)}
                                                                onChangeArabic={val => updateFieldAr('reminder_desc', val)}
                                                                rows={2}
                                                            />
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>

                                        <SectionHeader title="Recruitment Contact Desk" icon={Mail} sectionId="contact" />
                                        <AnimatePresence>
                                            {expandedSections.includes('Recruitment Contact Desk') && (
                                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                                    <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6 ml-4 mb-8">
                                                        <div className="space-y-6">
                                                            <BilingualInput 
                                                                label="Sidebar Topic Title"
                                                                englishValue={content.Careers?.sidebar_title}
                                                                arabicValue={content.Careers?.sidebar_title_ar}
                                                                onChangeEnglish={val => updateField('sidebar_title', val)}
                                                                onChangeArabic={val => updateFieldAr('sidebar_title', val)}
                                                            />
                                                            <BilingualInput 
                                                                label="Central CV Desk Email"
                                                                englishValue={content.Careers?.sidebar_email}
                                                                arabicValue={content.Careers?.sidebar_email_ar}
                                                                onChangeEnglish={val => updateField('sidebar_email', val)}
                                                                onChangeArabic={val => updateFieldAr('sidebar_email', val)}
                                                            />
                                                            <BilingualTextArea 
                                                                label="CV Submission Prompt"
                                                                englishValue={content.Careers?.cv_text}
                                                                arabicValue={content.Careers?.cv_text_ar}
                                                                onChangeEnglish={val => updateField('cv_text', val)}
                                                                onChangeArabic={val => updateFieldAr('cv_text', val)}
                                                                rows={2}
                                                            />
                                                            <BilingualTextArea 
                                                                label="Portal Referral Text"
                                                                englishValue={content.Careers?.sidebar_desc}
                                                                arabicValue={content.Careers?.sidebar_desc_ar}
                                                                onChangeEnglish={val => updateField('sidebar_desc', val)}
                                                                onChangeArabic={val => updateFieldAr('sidebar_desc', val)}
                                                                rows={2}
                                                            />
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>

                                        <SectionHeader title="Corporate Workforce Values" icon={Users} sectionId="paragraphs" />
                                        <AnimatePresence>
                                            {expandedSections.includes('Corporate Workforce Values') && (
                                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                                    <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6 ml-4 mb-8">
                                                        {[1, 2, 3, 4].map(i => (
                                                            <div key={i}>
                                                                <BilingualTextArea 
                                                                    label={`Value Narrative ${i}`}
                                                                    englishValue={content.Careers?.[`content_p${i}`]}
                                                                    arabicValue={content.Careers?.[`content_p${i}_ar`]}
                                                                    onChangeEnglish={val => updateField(`content_p${i}`, val)}
                                                                    onChangeArabic={val => updateFieldAr(`content_p${i}`, val)}
                                                                    rows={3}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>

                                        <SectionHeader title="Future Opportunities" icon={Target} sectionId="opp" />
                                        <AnimatePresence>
                                            {expandedSections.includes('Future Opportunities') && (
                                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                                    <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6 ml-4 mb-8">
                                                        <div className="space-y-6">
                                                            <BilingualInput 
                                                                label="Focus Title"
                                                                englishValue={content.Careers?.opp_title}
                                                                arabicValue={content.Careers?.opp_title_ar}
                                                                onChangeEnglish={val => updateField('opp_title', val)}
                                                                onChangeArabic={val => updateFieldAr('opp_title', val)}
                                                            />
                                                            <BilingualTextArea 
                                                                label="Global Reach Summary"
                                                                englishValue={content.Careers?.opp_desc}
                                                                arabicValue={content.Careers?.opp_desc_ar}
                                                                onChangeEnglish={val => updateField('opp_desc', val)}
                                                                onChangeArabic={val => updateFieldAr('opp_desc', val)}
                                                                rows={3}
                                                            />
                                                            <BilingualInput 
                                                                label="Global Apply Button"
                                                                englishValue={content.Careers?.opp_btn_text}
                                                                arabicValue={content.Careers?.opp_btn_text_ar}
                                                                onChangeEnglish={val => updateField('opp_btn_text', val)}
                                                                onChangeArabic={val => updateFieldAr('opp_btn_text', val)}
                                                            />
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </motion.div>
                                ) : (
                                    <motion.div key="media" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }} className="space-y-12 pb-24">
                                        <div className="flex items-center justify-between border-b border-slate-200 pb-8">
                                            <div>
                                                <h3 className="text-3xl font-black text-slate-800 tracking-tight">Recruitment Media</h3>
                                                <p className="text-slate-500 text-sm font-medium">Standard recruitment visual catalog</p>
                                            </div>
                                            <button
                                                onClick={saveAllPendingImages}
                                                disabled={Object.keys(pendingImages).length === 0 || !!saving}
                                                className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-2xl font-bold transition-all shadow-xl shadow-blue-200 disabled:bg-slate-300 active:scale-95 flex items-center gap-2"
                                            >
                                                <Save className="w-4 h-4" />
                                                <span>Push Assets Live</span>
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                                            <UploadBox keyName="hero_image" label="Recruitment Banner (Primary)" currentImage={content.Careers?.hero_image} />
                                            <UploadBox keyName="notice_image" label="Recruitment Protocol (Official)" currentImage={content.Careers?.notice_image} />
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        )}
                        <div className="h-20" />
                    </div>
                </main>
            </div>

            {/* Notification Hud */}
            <AnimatePresence>
                {message.text && (
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 50 }}
                        className={`fixed bottom-10 right-10 z-[100] px-8 py-4 rounded-2xl shadow-2xl flex items-center gap-4 ${message.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}
                    >
                        {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                        <span className="font-bold text-sm tracking-tight">{message.text}</span>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AdminCareers;
